// @flow
import React from 'react';
import { AppLink } from '@finect/tabular-core/AppLink';
import { Paragraph } from '@finect/tabular-core/Paragraph';
import { H2, H3 } from '@finect/tabular-core/Headings';
import { ArticleDetail, Video, ArticleCTA, LazyPill } from '../../../components';

type Props = {
  location: Object,
  history: Object,
  toggleModal: Function
};

export const Ser = ({ location, history, toggleModal }: Props) => {
  const redirectTo = (url: string) => {
    history.push(url);
  };

  return (
    <ArticleDetail
      location={location}
      title="¿Cómo tiene que ser tu asesor financiero?"
      metaTitle="¿Cómo debe ser tu asesor financiero ideal? Claves para elegir bien"
      redirectTo={redirectTo}
      toggleModal={toggleModal}
      image="3f607498aeca492dae7d1e10b39fb357"
      schemaORG={{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '¿Qué certificaciones debe tener un asesor financiero confiable?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Un asesor financiero confiable suele contar con certificaciones reconocidas que avalan su formación y conocimientos, como EFPA, CFP (Certified Financial Planner) o titulaciones equivalentes en su país. Estas acreditaciones demuestran que el profesional ha pasado por rigurosos procesos de formación y está obligado a cumplir estándares éticos y profesionales.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Cómo saber si un asesor realmente actúa en tu mejor interés?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Un buen asesor siempre prioriza tus objetivos y bienestar financiero por encima de las comisiones o intereses propios. Actúa con transparencia, explica claramente los costes, riesgos y beneficios de cada producto, y se adapta a tus necesidades. La ética y la transparencia son los mejores indicadores de que el asesor realmente actúa en tu interés.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Es mejor un asesor independiente o uno vinculado a una entidad financiera?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Depende de tus necesidades. Un asesor independiente puede ofrecer productos de diferentes entidades y diseñar estrategias completamente adaptadas a ti, sin presiones comerciales. Un asesor vinculado a una entidad suele tener acceso a productos propios y puede ofrecer ventajas puntuales, pero sus recomendaciones podrían estar condicionadas por la entidad. La clave es conocer cómo se estructura su asesoramiento y verificar que tus intereses siempre estén protegidos.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Qué señales indican que deberías cambiar de asesor financiero?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Algunas señales de alerta incluyen: falta de transparencia sobre comisiones, recomendaciones que no se ajustan a tu perfil de riesgo, poca comunicación, jerga técnica que no entiendes y ausencia de un plan financiero personalizado. Si tu asesor no responde a tus dudas o no se adapta a tus necesidades, puede ser el momento de buscar un profesional más adecuado.'
            }
          },
          {
            '@type': 'Question',
            name: '¿Cómo evaluar si el asesor entiende tus objetivos y se adapta a tu perfil?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Un buen asesor realiza un análisis detallado de tu situación financiera, tus metas y tu tolerancia al riesgo antes de hacer cualquier recomendación. Escucha atentamente, hace preguntas relevantes y propone un plan personalizado que se ajusta a tu perfil. La adaptación a tus objetivos y la capacidad de explicarte las estrategias de forma clara son indicadores clave de un asesor que realmente comprende tu situación.'
            }
          }
        ]
      }}
    >
      <Video
        id="0c0TZ9xnyO8"
        src="https://www.youtube.com/embed/0c0TZ9xnyO8?hd=1&showinfo=0&controls=0&rel=0&enablejsapi=1"
      />
      <Paragraph>
        Los{' '}
        <AppLink href="https://www.finect.com/asesores?utm_source=Finect&utm_medium=Articulo&utm_campaign=Asesores&utm_term=como_tiene_que_ser">
          asesores financieros
        </AppLink>{' '}
        no son adivinos. No saben qué harán los mercados en los próximos meses ni poseen información privilegiada que
        les permita garantizar ganancias rápidas. Si algún profesional promete “el pelotazo seguro” en bolsa con sus
        servicios, lo mejor es desconfiar.
      </Paragraph>
      <Paragraph>
        Tu <b>asesor o experto financiero ideal</b> debe ser un profesional cuyo único interés sea tu bienestar
        financiero. Su objetivo es comprender tus metas, ya sean comprar una casa más grande, pagar los estudios de tus
        hijos, asegurarte una jubilación tranquila o dejar una buena herencia a tu familia. A partir de esta
        información, te ayudará a organizar tus ahorros e inversiones para que alcances esos objetivos de manera
        ordenada y eficiente.
      </Paragraph>
      <Paragraph>
        Es fundamental distinguir entre un{' '}
        <b>
          <AppLink href="https://www.finect.com/asesores?utm_source=Finect&utm_medium=Articulo&utm_campaign=Asesores&utm_term=como_tiene_que_ser">
            asesor financiero
          </AppLink>
        </b>{' '}
        y un <b>vendedor de productos financieros</b>. El primero escucha atentamente tus necesidades, analiza tu
        situación y crea un plan personalizado que se adapte a ti. El segundo, insistirá en que compres un producto
        específico porque “es el que mejor rentabilidad dará”, sin tener en cuenta tu perfil ni tus objetivos, y muchas
        veces usando jerga financiera complicada que dificulta la comprensión de sus propuestas.
      </Paragraph>
      <Paragraph>
        Un buen asesor te dirá que no puede predecir el comportamiento futuro de los activos, pero te propondrá una
        estrategia sólida para estar preparado ante cualquier eventualidad del mercado.
      </Paragraph>
      <Paragraph>
        Por último, la prueba del algodón del buen asesor llega con los momentos de volatilidad. Cuando los mercados
        caen y las carteras sufren fuertes pérdidas a corto plazo, el asesor profesional te contactará para
        tranquilizarte y recordarte que la volatilidad o los problemas temporales estaban dentro del plan.
      </Paragraph>

      <ArticleCTA targetId="solicita-asesor" toggleModal={toggleModal} />

      <Paragraph>
        En resumen, tu asesor ideal es aquel que te escucha, comprende tus metas, establece un plan para alcanzarlas y
        permanece a tu lado, especialmente en los peores momentos del mercado.
      </Paragraph>

      <section>
        <H2>¿Por qué es importante elegir bien a tu asesor financiero?</H2>
        <Paragraph>
          Elegir al asesor financiero adecuado es una de las decisiones más importantes que puedes tomar para gestionar
          tu dinero. No se trata solo de tener a alguien que gestione tus inversiones, sino de contar con un profesional
          que comprenda tus metas y te ayude a alcanzarlas, protegiendo tu patrimonio y maximizando tus oportunidades.{' '}
        </Paragraph>
        <Paragraph>
          Como decimos, un buen asesor financiero no promete fórmulas mágicas ni ganancias rápidas; su valor real radica
          en su capacidad para guiarte de manera profesional y personalizada.
        </Paragraph>
        <H3>El impacto en tus decisiones financieras</H3>
        <Paragraph>
          El experto va a influir de forma directa en tus decisiones financieras. No se trata de predecir los mercados
          ni de recomendar productos al azar, sino de ayudarte a organizar tus ahorros y planificar tus inversiones en
          función de tus objetivos de largo plazo.
        </Paragraph>
        <Paragraph>
          Las decisiones tomadas junto a un buen asesor suelen ser más coherentes, estratégicas y alineadas con tu
          perfil de riesgo, evitando movimientos impulsivos que puedan comprometer tu futuro.
        </Paragraph>
        <H3>Evitar errores costosos y fraudes</H3>
        <Paragraph>
          Un buen asesor también protege tu dinero frente a errores frecuentes y posibles fraudes. A veces nos dejamos
          llevar por recomendaciones de amigos, familiares o conocidos que pueden no estar basadas en un conocimiento
          profundo del mercado y nos llevan a tomar decisiones erróneas con perjuicio de nuestro capital.{' '}
        </Paragraph>
        <Paragraph>
          También podemos encontrarnos, especialmente en redes sociales, multitud de estafas a través de publicidad
          engañosa de plataformas de inversión, de cursos de trading o acciones y productos de inversión “infalibles”
          que pueden causarnos grandes pérdidas patrimoniales
        </Paragraph>
        <Paragraph>
          Un profesional te escuchará, analizará tu situación y adaptará su consejo a tus necesidades, sin prometerte la
          fórmula mágica -porque no existe-.{' '}
        </Paragraph>
        <H2>Cualidades clave de un buen asesor financiero</H2>
        <Paragraph>
          Elegir un asesor no es solo cuestión de confianza personal; también es fundamental que cuente con ciertas
          cualidades y competencias que garanticen un servicio profesional y seguro.
        </Paragraph>
        <H3>Formación y certificaciones</H3>
        <Paragraph>
          Un buen asesor financiero posee formación específica en finanzas, economía o áreas relacionadas, además de
          certificaciones profesionales reconocidas, como EFPA o CFA. Estas acreditaciones demuestran que cuenta con los
          conocimientos necesarios para ofrecer un servicio de calidad y seguir las mejores prácticas del sector.
        </Paragraph>
        <H3>Experiencia comprobada</H3>
        <Paragraph>
          La experiencia es clave. No basta con conocer la teoría; un buen asesor ha trabajado con clientes reales, ha
          enfrentado distintos ciclos de mercado y sabe cómo reaccionar ante las fluctuaciones del mercado. La
          experiencia permite anticipar problemas, ofrecer soluciones y adaptar las estrategias a cada tipo de cliente.
        </Paragraph>
        <H3>Ética y transparencia</H3>
        <Paragraph>
          Un asesor financiero debe actuar con integridad y priorizar siempre los intereses del cliente. La
          transparencia en los costes, comisiones y riesgos asociados a cada producto es imprescindible para generar
          confianza y garantizar decisiones responsables.
        </Paragraph>
        <H3>Escucha activa y empatía</H3>
        <Paragraph>
          Escuchar es más importante que hablar. Un buen asesor se interesa genuinamente por tus necesidades y
          objetivos, haciendo preguntas sobre tu situación financiera, tolerancia al riesgo y prioridades de vida. Este
          enfoque permite crear estrategias que realmente se adapten a ti.
        </Paragraph>
        <H3>Capacidad para explicar lo complejo de forma simple</H3>
        <Paragraph>
          Los mercados financieros pueden ser complicados y llenos de jerga técnica. Un buen asesor es capaz de explicar
          conceptos complejos de forma sencilla, para que comprendas las decisiones que tomas y no dependas únicamente
          de su criterio.
        </Paragraph>
        <H3>Orientación a tus objetivos, no a comisiones</H3>
        <Paragraph>
          El trabajo de un experto financiero comienza analizando tu situación financiera completa, incluyendo ingresos,
          gastos, patrimonio y objetivos a corto, medio y largo plazo. A partir de ahí, realiza un diagnóstico
          personalizado y define una estrategia adaptada a tu perfil de riesgo y a tus prioridades. Esto implica
          seleccionar productos financieros adecuados, combinarlos en una cartera diversificada y establecer un plan de
          seguimiento con revisiones periódicas para ajustarlo según cambien tus necesidades o las condiciones del
          mercado.{' '}
        </Paragraph>
        <Paragraph>
          En este escenario, su éxito se medirá por la capacidad de que este plan funcione en la práctica, ayudándote a
          alcanzar tus metas financieras de manera consistente y sostenible.
        </Paragraph>
        <H2>Preguntas que deberías hacer antes de contratar a un asesor</H2>
        <Paragraph>
          Antes de comprometerte con un asesor financiero, es esencial plantearle algunas preguntas clave para
          asegurarte de que encaja con tus necesidades:
        </Paragraph>
        <H3>¿Cuál es tu experiencia y formación?</H3>
        <Paragraph>
          Pregunta por sus estudios, certificaciones y experiencia previa. Esto te dará una idea de su preparación y
          capacidad para enfrentar distintas situaciones del mercado.
        </Paragraph>
        <H3>¿Cómo cobras por tus servicios?</H3>
        <Paragraph>
          Asegúrate de entender su modelo de remuneración: tarifa fija, por hora o porcentaje sobre el capital
          gestionado. Debes saber cómo se estructura el coste y que sea transparente, sin sorpresas ocultas.
        </Paragraph>
        <H3>¿Trabajas con productos de una sola entidad?</H3>
        <Paragraph>
          Tu asesor financiero ideal no debe limitarse a buscar comisiones; su foco principal es ayudarte a alcanzar tus
          objetivos financieros. Aunque muchos asesores trabajan de manera independiente, también existen expertos
          financieros que operan dentro de una entidad financiera pero que cuentan con un{' '}
          <b>catálogo amplio y diverso de productos</b>, más allá de los propios de su entidad. Así que saber cuánta
          libertad tiene para recomendar distintos productos marcará la diferencia.{' '}
        </Paragraph>
        <H3>¿Cómo es tu proceso de trabajo con un cliente?</H3>
        <Paragraph>
          Pregunta cómo va a analizar tu situación, cómo establece los objetivos y cómo realiza el seguimiento. Un buen
          asesor tiene un proceso estructurado que incluye evaluación inicial, definición de metas, diseño de la
          estrategia y revisión periódica, siempre adaptándose a tus circunstancias.
        </Paragraph>
      </section>

      <ArticleCTA targetId="solicita-asesor" toggleModal={toggleModal} />

      <section style={{ marginTop: '60px' }}>
        <H2>Preguntas frecuentes</H2>

        <H3>1. ¿Qué certificaciones debe tener un asesor financiero confiable?</H3>
        <Paragraph>
          Un asesor financiero confiable suele contar con certificaciones reconocidas que avalan su formación y
          conocimientos, como EFPA, CFP (Certified Financial Planner) o titulaciones equivalentes en su país. Estas
          acreditaciones demuestran que el profesional ha pasado por rigurosos procesos de formación y está obligado a
          cumplir estándares éticos y profesionales.
        </Paragraph>
        <H3>2. ¿Cómo saber si un asesor realmente actúa en tu mejor interés?</H3>
        <Paragraph>
          Un buen asesor siempre prioriza tus objetivos y bienestar financiero por encima de las comisiones o intereses
          propios. Actúa con transparencia, explica claramente los costes, riesgos y beneficios de cada producto, y se
          adapta a tus necesidades. La ética y la transparencia son los mejores indicadores de que el asesor realmente
          actúa en tu interés.
        </Paragraph>
        <H3>3. ¿Es mejor un asesor independiente o uno vinculado a una entidad financiera?</H3>
        <Paragraph>
          Depende de tus necesidades. Un <b>asesor independiente</b> puede ofrecer productos de diferentes entidades y
          diseñar estrategias completamente adaptadas a ti, sin presiones comerciales. Un{' '}
          <b>asesor vinculado a una entidad</b> suele tener acceso a productos propios y puede ofrecer ventajas
          puntuales, pero sus recomendaciones podrían estar condicionadas por la entidad. La clave es conocer cómo se
          estructura su asesoramiento y verificar que tus intereses siempre estén protegidos.
        </Paragraph>
        <H3>4. ¿Qué señales indican que deberías cambiar de asesor financiero?</H3>
        <Paragraph>
          Algunas señales de alerta incluyen: falta de transparencia sobre comisiones, recomendaciones que no se ajustan
          a tu perfil de riesgo, poca comunicación, jerga técnica que no entiendes y ausencia de un plan financiero
          personalizado. Si tu asesor no responde a tus dudas o no se adapta a tus necesidades, puede ser el momento de
          buscar un profesional más adecuado.
        </Paragraph>
        <H3>5. ¿Cómo evaluar si el asesor entiende tus objetivos y se adapta a tu perfil?</H3>
        <Paragraph>
          Un buen asesor realiza un análisis detallado de tu situación financiera, tus metas y tu tolerancia al riesgo
          antes de hacer cualquier recomendación. Escucha atentamente, hace preguntas relevantes y propone un plan
          personalizado que se ajusta a tu perfil. La adaptación a tus objetivos y la capacidad de explicarte las
          estrategias de forma clara son indicadores clave de un asesor que realmente comprende tu situación.
        </Paragraph>
      </section>
      <LazyPill />
    </ArticleDetail>
  );
};
